/**
 * @file api/web.ts
 * @description Web-browser-specific file API implementation.
 */

import { AppFileAPI } from '@hcie/core';
import { imageFormatRegistry } from '../format-registry';

export function buildWebFallbackApi(): AppFileAPI {
  return {
    openFile: (): Promise<string | null> =>
      new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.hcie,' + imageFormatRegistry.getAll().map((f: any) => f.extensions.join(",")).join(",");
        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) { resolve(null); return; }
          (window as any).lastSelectedFile = file;
          if (file.name) {
            (window as any).lastFileName = file.name;
            resolve(file.name);
          }
        };
        input.click();
      }),

    readFile: async (_filePath: string): Promise<string | null> => {
      const file = (window as any).lastSelectedFile;
      if (!file) { console.error('No file selected in Web Mode'); return null; }
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      });
    },

    readFileBinary: async (_filePath: string): Promise<Uint8Array | null> => {
      const file = (window as any).lastSelectedFile;
      if (!file) return null;
      const buf = await file.arrayBuffer();
      return new Uint8Array(buf);
    },

    saveFile: async (
      content: string | Uint8Array | ArrayBuffer,
      filePath: string | null,
      saveas: boolean,
      type: string
    ): Promise<string | null> => {
      const win = window as any;
      
      // 1. Modern Web Save (File System Access API)
      if (typeof win.showSaveFilePicker === 'function' && !saveas && win.lastFileHandle) {
        try {
          const handle = win.lastFileHandle;
          const writable = await handle.createWritable();
          await writable.write(content);
          await writable.close();
          return handle.name;
        } catch (e) {
          console.warn('FSAA failed, falling back to download:', e);
        }
      }

      // 2. Modern Save As
      if (typeof win.showSaveFilePicker === 'function' && (saveas || !win.lastFileHandle)) {
        try {
          const cleanExt = type.replace(".", "");
          const opts = {
            suggestedName: filePath || `untitled.${cleanExt}`,
            types: [{
              description: `${cleanExt.toUpperCase()} File`,
              accept: { [type === 'hcie' ? 'application/json' : `image/${cleanExt}`]: [`.${cleanExt}`] }
            }]
          };
          const handle = await win.showSaveFilePicker(opts);
          win.lastFileHandle = handle;
          const writable = await handle.createWritable();
          await writable.write(content);
          await writable.close();
          return handle.name;
        } catch (e) {
          console.warn('FSAA Save As failed or cancelled:', e);
        }
      }

      // 3. Traditional download fallback
      const a = document.createElement('a');
      let url: string;

      if (content instanceof Uint8Array || content instanceof ArrayBuffer) {
        const bytes = content instanceof Uint8Array ? content : new Uint8Array(content);
        const mimeMap: any = { 
          psd: 'image/vnd.adobe.photoshop', 
          hcie: 'application/json', 
          png: 'image/png', 
          jpg: 'image/jpeg', 
          jpeg: 'image/jpeg',
          gif: 'image/gif',
          bmp: 'image/bmp',
          webp: 'image/webp',
          tga: 'image/x-tga'
        };
        const cleanType = type.replace(".", "").toLowerCase();
        const blob = new Blob([bytes as unknown as BlobPart], { type: mimeMap[cleanType] ?? 'application/octet-stream' });
        url = URL.createObjectURL(blob);
      } else if (typeof content === 'string' && !content.startsWith('data:')) {
        const blob = new Blob([content], { type: 'application/json' });
        url = URL.createObjectURL(blob);
      } else {
        url = content as string;
      }

      let name = filePath ?? 'untitled';
      const expectedExt = type.startsWith('.') ? type : '.' + type;
      if (!name.toLowerCase().endsWith(expectedExt.toLowerCase())) name += expectedExt;
      
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      if (typeof url === 'string' && url.startsWith('blob:')) URL.revokeObjectURL(url);
      return name;
    },

    onMenuOpen: () => {},
    onMenuSave: () => {},
    onMenuSaveAs: () => {},
    onMenuExport: () => {},
    isDesktop: false
  };
}
